import { useState } from "react";
import { useI18n } from "@wato/i18n/react";
import { Button, cx } from "@wato/ui";
import { IconButton } from "./IconButton";
import { useApi } from "../api/context";
import { useSupportLauncherHidden } from "./support-launcher-visibility";

/**
 * WP-568 floating in-app support chat. Messages land in the shared `SupportTicket` model the
 * Super Admin inbox reads; the first message opens a ticket, later ones append to it.
 */
export function SupportWidget({ workspaceId }: { readonly workspaceId: string }) {
  const { t } = useI18n();
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [ticketId, setTicketId] = useState<string | undefined>();
  const [sent, setSent] = useState<readonly string[]>([]);
  const [state, setState] = useState<"idle" | "sending" | "error">("idle");
  const hidden = useSupportLauncherHidden();

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setState("sending");
    try {
      const r = await api.sendSupportMessage(workspaceId, {
        body,
        ...(ticketId ? { ticketId } : {}),
      });
      setTicketId(r.ticketId);
      setSent((s) => [...s, body]);
      setDraft("");
      setState("idle");
    } catch {
      setState("error");
    }
  };

  // Bottom offset: above the 4rem mobile bottom nav (plus the home-indicator safe area) so the
  // button never covers a nav item. It is taken out of view (not unmounted, so a half-written
  // message survives) while a screen with its own bottom-pinned composer is open.
  return (
    <div
      data-testid="support-launcher"
      data-hidden={hidden ? "true" : undefined}
      className={cx(
        "fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] end-4 z-40 md:bottom-4",
        hidden && "hidden",
      )}
    >
      {open ? (
        <section
          aria-label={t("helpCenter.supportWidget.title")}
          className="flex w-80 flex-col gap-2 rounded-lg border border-line bg-surface p-3 shadow-lg"
        >
          <header className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-fg">{t("helpCenter.supportWidget.title")}</h2>
            <IconButton
              variant="ghost"
              label={t("helpCenter.supportWidget.close")}
              icon="×"
              onClick={() => setOpen(false)}
            />
          </header>
          <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto text-sm">
            {sent.length === 0 ? (
              <li className="text-fg-muted">{t("helpCenter.supportWidget.intro")}</li>
            ) : (
              sent.map((m, i) => (
                <li key={i} className="self-end rounded-md bg-surface-muted px-2 py-1 text-fg">
                  {m}
                </li>
              ))
            )}
          </ul>
          {state === "error" ? (
            <p role="alert" className="text-xs text-fg">
              {t("helpCenter.supportWidget.error")}
            </p>
          ) : null}
          <textarea
            aria-label={t("helpCenter.supportWidget.placeholder")}
            placeholder={t("helpCenter.supportWidget.placeholder")}
            value={draft}
            maxLength={4000}
            onChange={(e) => setDraft(e.target.value)}
            className="min-h-16 rounded-md border border-line bg-surface p-2 text-sm text-fg"
          />
          <Button
            size="sm"
            disabled={state === "sending" || !draft.trim()}
            onClick={() => void send()}
          >
            {t("helpCenter.supportWidget.send")}
          </Button>
        </section>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          className="max-md:size-11 max-md:rounded-full max-md:p-0 opacity-80 hover:opacity-100"
          aria-label={t("helpCenter.supportWidget.open")}
          onClick={() => setOpen(true)}
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="size-5 shrink-0 md:hidden"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <span className="max-md:sr-only">{t("helpCenter.supportWidget.open")}</span>
        </Button>
      )}
    </div>
  );
}
